<script>
  import Icon from "@iconify/svelte";
  import { onMount } from "svelte";
  import { t } from "../lib/i18n.svelte.js";
  import { api } from "../lib/api.svelte.js";
  import { addToast } from "../components/Toast.svelte";
  import Layout from "../components/Layout.svelte";
  import SlideOver from "../components/SlideOver.svelte";
  import Skeleton from "../components/Skeleton.svelte";
  import EmptyState from "../components/EmptyState.svelte";

  let baseDomain = $state("");
  let invites = $state([]);
  let total = $state(0);
  let initialLoad = $state(true);
  let error = $state("");

  let search = $state("");
  let page = $state(1);
  let perPage = $state(15);
  let sortField = $state("created_at");
  let sortOrder = $state("desc");

  let totalPages = $derived(Math.max(1, Math.ceil(total / perPage)));

  let pageNumbers = $derived.by(() => {
    const tp = totalPages;
    const cur = page;
    if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1);
    const pages = [];
    pages.push(1);
    if (cur > 3) pages.push("...");
    for (let i = Math.max(2, cur - 1); i <= Math.min(tp - 1, cur + 1); i++) {
      pages.push(i);
    }
    if (cur < tp - 2) pages.push("...");
    pages.push(tp);
    return pages;
  });

  // Drawer state
  let drawerItem = $state(null);
  let revoking = $state(false);

  function inviteStatus(inv) {
    if (inv.revoked_at) return "revoked";
    if (inv.claimed_at) return "claimed";
    if (new Date(inv.expires_at) < new Date()) return "expired";
    return "pending";
  }

  const statusColors = {
    claimed: "text-green-500",
    revoked: "text-rose-500",
    expired: "text-zinc-500",
    pending: "text-amber-400",
  };

  const statusIcons = {
    claimed: "mdi:check-circle",
    revoked: "mdi:close-circle",
    expired: "mdi:clock-outline",
    pending: "mdi:timer-sand",
  };

  function setSort(field) {
    if (sortField === field) {
      sortOrder = sortOrder === "asc" ? "desc" : "asc";
    } else {
      sortField = field;
      sortOrder = "asc";
    }
    page = 1;
    fetchData();
  }

  function sortIndicator(field) {
    if (sortField !== field) return "";
    return sortOrder === "asc" ? " \u25B2" : " \u25BC";
  }

  async function fetchData() {
    try {
      const q = encodeURIComponent(search);
      const res = await api.get(`/invites?page=${page}&per_page=${perPage}&q=${q}&sort=${sortField}&order=${sortOrder}`);
      invites = res.data || [];
      total = res.total || 0;
    } catch (e) {
      if (initialLoad) error = e.message || t("invites.failed_load");
    }
    initialLoad = false;
  }

  function handleSearch() {
    page = 1;
    fetchData();
  }

  function goPage(p) {
    page = p;
    fetchData();
  }

  function handlePerPage(val) {
    perPage = Number(val);
    page = 1;
    fetchData();
  }

  function openDrawer(invite) {
    drawerItem = invite;
  }

  function closeDrawer() {
    drawerItem = null;
  }

  async function handleRevoke() {
    if (!drawerItem) return;
    revoking = true;
    try {
      await api.post(`/invites/${drawerItem.id}/revoke`);
      addToast(t("invites.revoke_success"), "success");
      await fetchData();
      const updated = invites.find((i) => i.id === drawerItem.id);
      if (updated) openDrawer(updated);
      else closeDrawer();
    } catch (e) {
      addToast(e.message || t("invites.revoke_failed"), "error");
    }
    revoking = false;
  }

  function formatDate(d) {
    if (!d) return "-";
    return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }

  function formatDateTime(d) {
    if (!d) return "-";
    return new Date(d).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  }

  function truncToken(inv) {
    const hash = inv.token_hash || inv.token || "";
    if (!hash) return "-";
    return hash.substring(0, 8) + "...";
  }

  function inviteUrl(inv) {
    const slug = inv.site_slug || "";
    const hash = inv.token_hash || "";
    if (!slug || !baseDomain || !hash) return "";
    return `${slug}.${baseDomain}?claim=${hash}`;
  }

  onMount(async () => {
    try {
      const setup = await api.get("/setup");
      const bd = (setup?.env || []).find(e => e.name === "BASE_DOMAIN");
      if (bd) baseDomain = bd.value;
    } catch {}
    fetchData();
    const id = setInterval(() => {
      if (document.visibilityState !== "hidden") fetchData();
    }, 10000);
    return () => clearInterval(id);
  });
</script>

<Layout>
  <div class="{drawerItem ? 'lg:mr-[480px]' : ''} transition-all">
    <div class="ui-card p-5 sm:p-6">
      <!-- Header -->
      <div class="flex items-start gap-3 mb-6">
        <span class="ui-header-icon">
          <Icon icon="mdi:email-outline" class="w-[18px] h-[18px]" />
        </span>
        <div class="flex-1">
          <h1 class="text-lg font-normal tracking-wide text-zinc-100">{t("invites.title")}</h1>
          <p class="mt-0.5 text-sm text-zinc-400">{t("invites.subtitle")}</p>
        </div>
      </div>

      <!-- Control bar -->
      <div class="flex items-center gap-3 flex-wrap mb-4">
        <!-- Search -->
        <div class="flex-1 min-w-[180px]">
          <input
            type="text"
            class="w-full h-9 px-3 bg-black/20 border border-white/10 rounded text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-white/25"
            placeholder={t("common.search_placeholder")}
            bind:value={search}
            oninput={handleSearch}
          />
        </div>

        <!-- Per page -->
        <select
          class="h-9 px-2 bg-black/20 border border-white/10 rounded text-sm text-zinc-300 focus:outline-none focus:border-white/25 cursor-pointer"
          value={perPage}
          onchange={(e) => handlePerPage(e.target.value)}
        >
          <option value={15}>15</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
        </select>

        <!-- Refresh -->
        <button
          class="h-9 w-9 flex items-center justify-center bg-black/20 border border-white/10 rounded text-zinc-400 hover:text-white hover:border-white/25 transition-colors"
          onclick={fetchData}
          title={t("common.refresh")}
        >
          <Icon icon="mdi:refresh" class="w-4 h-4" />
        </button>

        <!-- Pagination -->
        <div class="flex items-center gap-1">
          <button
            class="h-8 w-8 flex items-center justify-center rounded text-sm text-zinc-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={page <= 1}
            onclick={() => goPage(page - 1)}
          >&lsaquo;</button>
          {#each pageNumbers as p}
            {#if p === "..."}
              <span class="h-8 w-8 flex items-center justify-center text-sm text-zinc-600">&hellip;</span>
            {:else}
              <button
                class="h-8 w-8 flex items-center justify-center rounded text-sm transition-colors {p === page ? 'bg-white/10 text-white font-medium' : 'text-zinc-400 hover:text-white hover:bg-white/10'}"
                onclick={() => goPage(p)}
              >{p}</button>
            {/if}
          {/each}
          <button
            class="h-8 w-8 flex items-center justify-center rounded text-sm text-zinc-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={page >= totalPages}
            onclick={() => goPage(page + 1)}
          >&rsaquo;</button>
        </div>
      </div>

      {#if error}
        <div class="px-3 py-2 rounded border border-rose-500/30 bg-rose-500/10 text-sm text-rose-200 mb-4">
          {error}
        </div>
      {/if}

      <!-- Table -->
      {#if initialLoad}
        <Skeleton lines={8} />
      {:else if invites.length === 0}
        <EmptyState message={t("invites.no_invites")} />
      {:else}
        <!-- Mobile: card layout -->
        <div class="md:hidden space-y-2">
          {#each invites as invite (invite.id)}
            {@const status = inviteStatus(invite)}
            <button
              class="w-full text-left p-4 rounded-lg border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
              onclick={() => openDrawer(invite)}
            >
              <div class="flex items-center justify-between mb-2">
                <span class="inline-flex items-center gap-2 text-white font-medium text-sm truncate mr-2">
                  <Icon icon="mdi:email-outline" class="w-4 h-4 text-zinc-600 shrink-0" />
                  {invite.viewer_label || (invite.viewer_id ? invite.viewer_id.substring(0, 8) + "..." : "-")}
                </span>
                <span class="inline-flex items-center gap-1.5 shrink-0 {statusColors[status]}">
                  <Icon icon={statusIcons[status]} class="w-4 h-4" />
                  <span class="text-xs capitalize">{status}</span>
                </span>
              </div>
              <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <span class="text-zinc-500">{t("invites.token")}</span>
                <span class="text-zinc-400 font-mono">{truncToken(invite)}</span>
                <span class="text-zinc-500">{t("invites.expires")}</span>
                <span class="text-zinc-400">{formatDate(invite.expires_at)}</span>
                <span class="text-zinc-500">{t("invites.created")}</span>
                <span class="text-zinc-400">{formatDate(invite.created_at)}</span>
              </div>
            </button>
          {/each}
        </div>

        <!-- Desktop: table layout -->
        <div class="hidden md:block overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-white/10">
                <th class="text-left py-3 px-4 text-xs uppercase tracking-wide text-zinc-500 font-medium cursor-pointer hover:text-zinc-300 select-none" onclick={() => setSort("viewer_label")}>
                  {t("invites.viewer")}{sortIndicator("viewer_label")}
                </th>
                <th class="text-left py-3 px-4 text-xs uppercase tracking-wide text-zinc-500 font-medium">
                  {t("invites.token")}
                </th>
                <th class="text-left py-3 px-4 text-xs uppercase tracking-wide text-zinc-500 font-medium cursor-pointer hover:text-zinc-300 select-none" onclick={() => setSort("status")}>
                  {t("invites.status")}{sortIndicator("status")}
                </th>
                <th class="text-left py-3 px-4 text-xs uppercase tracking-wide text-zinc-500 font-medium cursor-pointer hover:text-zinc-300 select-none" onclick={() => setSort("expires_at")}>
                  {t("invites.expires")}{sortIndicator("expires_at")}
                </th>
                <th class="text-left py-3 px-4 text-xs uppercase tracking-wide text-zinc-500 font-medium cursor-pointer hover:text-zinc-300 select-none" onclick={() => setSort("created_at")}>
                  {t("invites.created")}{sortIndicator("created_at")}
                </th>
              </tr>
            </thead>
            <tbody>
              {#each invites as invite (invite.id)}
                {@const status = inviteStatus(invite)}
                <tr
                  class="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors"
                  onclick={() => openDrawer(invite)}
                >
                  <td class="py-3 px-4 text-white font-medium"><span class="inline-flex items-center gap-2"><Icon icon="mdi:email-outline" class="w-4 h-4 text-zinc-600 shrink-0" />{invite.viewer_label || (invite.viewer_id ? invite.viewer_id.substring(0, 8) + "..." : "-")}</span></td>
                  <td class="py-3 px-4 text-zinc-400 font-mono text-xs">{truncToken(invite)}</td>
                  <td class="py-3 px-4">
                    <span class="inline-flex items-center gap-1.5 {statusColors[status]}">
                      <Icon icon={statusIcons[status]} class="w-4 h-4" />
                      <span class="text-xs capitalize">{status}</span>
                    </span>
                  </td>
                  <td class="py-3 px-4 text-zinc-500 whitespace-nowrap">{formatDate(invite.expires_at)}</td>
                  <td class="py-3 px-4 text-zinc-500 whitespace-nowrap">{formatDate(invite.created_at)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </div>
  </div>

  <!-- Detail drawer -->
  {#if drawerItem}
    {@const status = inviteStatus(drawerItem)}
    <SlideOver onclose={closeDrawer} title={t("invites.details")} width="480px">
      <div class="space-y-6">
        <dl class="space-y-3 text-sm">
          <div>
            <dt class="text-zinc-500">{t("invites.viewer")}</dt>
            <dd class="text-zinc-200">{drawerItem.viewer_label || (drawerItem.viewer_id ? drawerItem.viewer_id.substring(0, 8) + "..." : "-")}</dd>
          </div>
          <div>
            <dt class="text-zinc-500">{t("invites.token")}</dt>
            <dd class="text-zinc-200 font-mono text-xs break-all">{drawerItem.token_hash || drawerItem.token || "-"}</dd>
          </div>
          <div>
            <dt class="text-zinc-500 mb-1.5">{t("invites.invite_url")}</dt>
            <dd>
              {#if inviteUrl(drawerItem)}
                <div class="flex gap-2">
                  <input type="text" readonly value={inviteUrl(drawerItem)} class="flex-1 h-9 px-3 bg-black/20 border border-white/10 rounded text-xs text-zinc-300 font-mono" />
                  <button class="ui-btn !px-3 !py-0" onclick={() => { navigator.clipboard.writeText(inviteUrl(drawerItem)); addToast(t("invites.copied"), "success"); }}>
                    <Icon icon="mdi:content-copy" class="w-4 h-4" />
                  </button>
                </div>
              {:else}
                <span class="text-xs text-zinc-500 font-mono">{drawerItem.token_hash || "-"}</span>
              {/if}
            </dd>
          </div>
          <div>
            <dt class="text-zinc-500">{t("invites.status")}</dt>
            <dd>
              <span class="inline-flex items-center gap-1.5 {statusColors[status]}">
                <Icon icon={statusIcons[status]} class="w-4 h-4" />
                <span class="capitalize">{status}</span>
              </span>
            </dd>
          </div>
          <div>
            <dt class="text-zinc-500">{t("invites.expires")}</dt>
            <dd class="text-zinc-200">{formatDateTime(drawerItem.expires_at)}</dd>
          </div>
          {#if drawerItem.claimed_at}
            <div>
              <dt class="text-zinc-500">{t("invites.claimed")}</dt>
              <dd class="text-zinc-200">{formatDateTime(drawerItem.claimed_at)}</dd>
            </div>
          {/if}
          {#if drawerItem.revoked_at}
            <div>
              <dt class="text-zinc-500">{t("invites.revoked_label")}</dt>
              <dd class="text-zinc-200">{formatDateTime(drawerItem.revoked_at)}</dd>
            </div>
          {/if}
          <div>
            <dt class="text-zinc-500">{t("invites.created")}</dt>
            <dd class="text-zinc-200">{formatDateTime(drawerItem.created_at)}</dd>
          </div>
        </dl>

        {#if status !== "revoked"}
          <div class="border-t border-white/10 pt-5">
            <button class="w-full inline-flex items-center justify-center gap-2 h-9 rounded bg-rose-500 text-white text-sm font-medium hover:bg-rose-400 transition-colors" onclick={handleRevoke} disabled={revoking}>
              <Icon icon="mdi:link-off" class="w-4 h-4" />
              {revoking ? t("invites.revoking") : t("invites.revoke")}
            </button>
          </div>
        {/if}
      </div>
    </SlideOver>
  {/if}
</Layout>
